import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AgGridTable from '../../components/AgGridTable';
import SidebarPanel from '../../components/admin/SidebarPanel';
import EducationalTooltip from '../../components/shared/EducationalTooltip';
import RelationshipBadge from '../../components/shared/RelationshipBadge';
import ModelDocumentationModal from '../../components/shared/ModelDocumentationModal';
import AlertMessage from '../../components/shared/alertMessage';
import { adminApi } from '../../services/api';

const SalesTeamsPage = () => {
  const navigate = useNavigate();
  const { teamId } = useParams();
  const gridRef = useRef(null);

  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showPanel, setShowPanel] = useState(false);
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [createMode, setCreateMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alertState, setAlertState] = useState({ show: false, type: 'info', title: '', message: '' });
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ organization_id: '', is_active: 'true', search: '' });
  const [organizations, setOrganizations] = useState([]);
  const [salesUsers, setSalesUsers] = useState([]);
  const [formData, setFormData] = useState({
    name: '', organization_id: '', team_type: '', manager_id: null,
    region: '', team_email: '', is_active: true
  });

  useEffect(() => {
    fetchOrganizations();
    fetchSalesUsers();
  }, []);

  useEffect(() => {
    fetchTeams();
  }, [filters]);

  useEffect(() => {
    if (teamId && teams.length > 0) {
      const team = teams.find(t => t.id == teamId);
      if (team) handleRowClick({ data: team });
    }
  }, [teamId, teams]);

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v));
      const res = await adminApi.getSalesTeams(params);
      setTeams(res.data?.teams || []);
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Failed to load teams', message: err.response?.data?.message || err.message });
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const res = await adminApi.getOrganizations({ is_active: 'true' });
      setOrganizations(res.data?.organizations || []);
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Failed to load organizations', message: err.response?.data?.message || err.message });
    }
  };

  const fetchSalesUsers = async () => {
    try {
      const res = await adminApi.getSalesUsers({ is_active: 'true' });
      setSalesUsers(res.data?.users || []);
    } catch (err) {
      setAlertState({ show: true, type: 'error', title: 'Failed to load users', message: err.response?.data?.message || err.message });
    }
  };

  const handleRowClick = useCallback((event) => {
    setSelectedTeam(event.data);
    setShowPanel(true);
    setEditMode(false);
    setCreateMode(false);
    navigate(`/admin/sales/teams/${event.data.id}`, { replace: true });
  }, [navigate]);

  const handleClosePanel = () => {
    setShowPanel(false);
    setSelectedTeam(null);
    setEditMode(false);
    setCreateMode(false);
    navigate('/admin/sales/teams', { replace: true });
  };

  const handleCellValueChanged = async (event) => {
    try {
      const { data, colDef, newValue } = event;
      await adminApi.updateSalesTeam(data.id, { [colDef.field]: newValue });
      fetchTeams();
    } catch (err) {
      alert('Failed to update: ' + (err.response?.data?.message || err.message));
      fetchTeams();
    }
  };

  const handleExport = () => {
    gridRef.current?.api.exportDataAsCsv({ fileName: `sales-teams-${new Date().toISOString().split('T')[0]}.csv` });
  };

  const handleAddTeam = () => {
    setFormData({
      name: '', organization_id: '', team_type: '', manager_id: null,
      region: '', team_email: '', is_active: true
    });
    setSelectedTeam(null);
    setCreateMode(true);
    setEditMode(false);
    setShowPanel(true);
  };

  const handleEditTeam = () => {
    if (selectedTeam) {
      setFormData({
        name: selectedTeam.name || '',
        organization_id: selectedTeam.organization?.id || '',
        team_type: selectedTeam.team_type || '',
        manager_id: selectedTeam.manager?.id || null,
        region: selectedTeam.region || '',
        team_email: selectedTeam.team_email || '',
        is_active: selectedTeam.is_active !== false
      });
      setEditMode(true);
      setCreateMode(false);
    }
  };

  const handleSaveTeam = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      if (editMode && selectedTeam) {
        await adminApi.updateSalesTeam(selectedTeam.id, formData);
        const res = await adminApi.getSalesTeams({});
        const updatedTeam = res.data?.teams?.find(t => t.id === selectedTeam.id);
        setSelectedTeam(updatedTeam || selectedTeam);
        setEditMode(false);
      } else if (createMode) {
        const res = await adminApi.createSalesTeam(formData);
        const newTeam = res.data?.team;
        if (newTeam) {
          setSelectedTeam(newTeam);
          navigate(`/admin/sales/teams/${newTeam.id}`, { replace: true });
        }
        setCreateMode(false);
      }
      fetchTeams();
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTeam = async () => {
    if (!selectedTeam) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedTeam.name}?`)) return;
    try {
      await adminApi.deleteSalesTeam(selectedTeam.id);
      handleClosePanel();
      fetchTeams();
    } catch (err) {
      alert('Failed to delete: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const columnDefs = [
    { headerName: 'Name', field: 'name', width: 200, editable: true },
    { headerName: 'Organization', field: 'organization.name', width: 200 },
    { headerName: 'Type', field: 'team_type', width: 120 },
    { headerName: 'Manager', field: 'manager_name', width: 150 },
    { headerName: 'Region', field: 'region', width: 120 },
    { headerName: 'Members', field: 'members_count', width: 100 },
    {
      headerName: 'Status',
      field: 'is_active',
      width: 100,
      cellRenderer: p => (
        <span className={`badge bg-${p.data?.is_active ? 'success' : 'danger'}`}>
          {p.data?.is_active ? 'Active' : 'Inactive'}
        </span>
      )
    }
  ];

  return (
    <>
      {alertState.show && (
        <AlertMessage
          type={alertState.type}
          title={alertState.title}
          message={alertState.message}
          dismissible={true}
          onDismiss={() => setAlertState({ ...alertState, show: false })}
          className="mb-3"
        />
      )}
      <div className="page-title-box d-flex align-items-center justify-content-between mb-3">
        <div className="col-8">
          <h4 className="text-lg">
            Sales Teams
            <EducationalTooltip title="Organize sales users into teams by organization, region, or product line." />
          </h4>
          <p className="text-muted">Manage sales team structure and assignments</p>
        </div>
        <div className="col-4 d-flex justify-content-end">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb m-0">
              <li className="breadcrumb-item"><a href="/admin/sales">Sales</a></li>
              <li className="breadcrumb-item active">Teams</li>
            </ol>
          </nav>
        </div>
      </div>

      <div className="d-flex justify-content-end align-items-center mb-3">
        <button className="btn btn-primary" onClick={handleAddTeam}>
          <i className="fas fa-plus me-1" />Create New
        </button>
      </div>
      <div className='card'>
        <div className="d-flex d-flex-inline justify-content-between p-2 mb-3">
          <div className='d-flex gap-2'>
            <select className="form-select form-select-sm" value={filters.organization_id} onChange={e => setFilters({...filters, organization_id: e.target.value})}>
              <option value="">All Organizations</option>
              {organizations.map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
            <select className="form-select form-select-sm" value={filters.is_active} onChange={e => setFilters({...filters, is_active: e.target.value})}>
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
            <input type="text" style={{width:'300px',maxWidth:'500px'}} className="form-control form-control-sm" placeholder="Search..." value={filters.search} onChange={e => setFilters({...filters, search: e.target.value})} />
            <button className='btn btn-secondary btn-sm'><i className='fas fa-search'></i></button>
          </div>
          <div className='d-flex gap-2'>
            <button className="btn btn-sm btn-info" onClick={() => setShowDocsModal(true)}>
              <i className="fas fa-book me-1" />Docs
            </button>
            <button className="btn btn-sm btn-success" onClick={handleExport}>
              <i className="fas fa-file-export me-1" />Export
            </button>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {loading ? (
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : teams.length === 0 ? (
          <div className="alert text-secondary text-center">
            <p>No sales teams found.</p>
          </div>
        ) : (
          <AgGridTable
            ref={gridRef}
            rowData={teams}
            columnDefs={columnDefs}
            onRowClicked={handleRowClick}
            onCellValueChanged={handleCellValueChanged}
            defaultColDef={{
              flex: 1,
              minWidth: 100,
              resizable: true,
              sortable: true,
              filter: true,
            }}
            gridOptions={{
              autoSizeStrategy: {
                type: 'fitGridWidth'
              }
            }}
            getRowId={(params) => params.data.id}
          />
        )}
      </div>
      <SidebarPanel
        isOpen={showPanel}
        onClose={handleClosePanel}
        title={createMode ? "Create Sales Team" : (editMode ? "Edit Sales Team" : "Sales Team Details")}
        className="sales-team-sidebar"
      >
        {createMode ? (
          <SalesTeamForm
            formData={formData}
            organizations={organizations}
            salesUsers={salesUsers}
            saving={saving}
            onChange={handleFormChange}
            onSave={handleSaveTeam}
            onCancel={handleClosePanel}
            isCreate={true}
          />
        ) : selectedTeam && editMode ? (
          <SalesTeamForm
            formData={formData}
            organizations={organizations}
            salesUsers={salesUsers}
            saving={saving}
            onChange={handleFormChange}
            onSave={handleSaveTeam}
            onCancel={() => setEditMode(false)}
            isCreate={false}
          />
        ) : selectedTeam && (
          <div className="row">
            <div className="col-12">
              <div className="card overflow-hidden">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="text-primary mb-0">{selectedTeam.name}</h5>
                    <div className="btn-group btn-group-sm">
                      <button className="btn btn-primary" onClick={handleEditTeam}>
                        <i className="fa fa-pencil me-1" />Edit
                      </button>
                      <button className="btn btn-danger" onClick={handleDeleteTeam}>
                        <i className="far fa-trash-can me-1" />Delete
                      </button>
                    </div>
                  </div>

                  <div className="pt-4">
                    <div className="row">
                      <div className="col-6">
                        <h5 className="font-size-15">Type:</h5>
                        <p className="text-muted mb-0">{selectedTeam.team_type || 'N/A'}</p>
                      </div>
                      <div className="col-6">
                        <h5 className="font-size-15">Status:</h5>
                        <p className="mb-0">
                          <span className={`badge bg-${selectedTeam.is_active ? 'success' : 'danger'}`}>
                            {selectedTeam.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </p>
                      </div>
                    </div>

                    <div className="row mt-4">
                      <div className="col-12">
                        <h5 className="font-size-15">Organization:</h5>
                        {selectedTeam.organization ? (
                          <RelationshipBadge label={selectedTeam.organization.name} icon="fa-building" variant="info" />
                        ) : <p className="text-muted mb-0">N/A</p>}
                      </div>
                    </div>

                    {selectedTeam.manager && (
                      <div className="mt-4">
                        <h5 className="font-size-15">Manager:</h5>
                        <RelationshipBadge label={selectedTeam.manager_name} icon="fa-user-tie" variant="primary" />
                      </div>
                    )}

                    <div className="row mt-4">
                      <div className="col-12">
                        <h5 className="font-size-15">Region:</h5>
                        <p className="text-muted mb-0">{selectedTeam.region || 'N/A'}</p>
                      </div>
                    </div>

                    <div className="row mt-4">
                      <div className="col-12">
                        <h5 className="font-size-15">Team Email:</h5>
                        <p className="text-muted mb-0">
                          {selectedTeam.team_email ? (
                            <a href={`mailto:${selectedTeam.team_email}`}>
                              {selectedTeam.team_email}
                            </a>
                          ) : 'N/A'}
                        </p>
                      </div>
                    </div>

                    <div className="mt-4">
                      <h5 className="font-size-15">Created At:</h5>
                      <p className="text-muted mb-0">{new Date(selectedTeam.created_at).toLocaleString()}</p>
                    </div>

                    <div className="mt-4">
                      <h5 className="font-size-15">Last Updated:</h5>
                      <p className="text-muted mb-0">{new Date(selectedTeam.updated_at).toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <h4 className="card-title text-primary mb-4">Statistics</h4>
                  <div className="row">
                    <div className="col-12 mb-3">
                      <div className="d-flex">
                        <div className="avatar-xs me-3">
                          <span className="avatar-title rounded-circle bg-primary bg-soft text-primary font-size-18">
                            <i className="fa fa-users"></i>
                          </span>
                        </div>
                        <div>
                          <h5 className="font-size-14 mb-0">{selectedTeam.members_count || 0}</h5>
                          <p className="text-muted mb-0">Team Members</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </SidebarPanel>

      <ModelDocumentationModal show={showDocsModal} onHide={() => setShowDocsModal(false)} modelType="sales_team" />
    </>
  );
};

const SalesTeamForm = ({ formData, organizations, salesUsers, saving, onChange, onSave, onCancel, isCreate }) => (
  <div className="row">
    <div className="col-12">
      <div className="card">
        <div className="card-body">
          <form onSubmit={onSave}>
            <div className="mb-3">
              <label className="form-label">Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                value={formData.name}
                onChange={e => onChange('name', e.target.value)}
                required
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Organization <span className="text-danger">*</span></label>
              <select
                className="form-select"
                value={formData.organization_id}
                onChange={e => onChange('organization_id', e.target.value)}
                required
              >
                <option value="">Select Organization</option>
                {organizations.map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Type</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.team_type}
                  onChange={e => onChange('team_type', e.target.value)}
                  placeholder="e.g., Regional, Product-focused"
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Region</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.region}
                  onChange={e => onChange('region', e.target.value)}
                  placeholder="e.g., Northeast, West Coast"
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Manager</label>
              <select
                className="form-select"
                value={formData.manager_id || ''}
                onChange={e => onChange('manager_id', e.target.value || null)}
              >
                <option value="">No Manager Assigned</option>
                {salesUsers.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.user?.first_name} {user.user?.last_name} ({user.user?.username})
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">Team Email</label>
              <input
                type="email"
                className="form-control"
                value={formData.team_email}
                onChange={e => onChange('team_email', e.target.value)}
                placeholder="team@example.com"
              />
            </div>

            <div className="mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={e => onChange('is_active', e.target.checked)}
                  id="isActive"
                />
                <label className="form-check-label" htmlFor="isActive">
                  Active
                </label>
              </div>
            </div>

            <div className="d-flex gap-2">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" />
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fa fa-save me-1" />
                    {isCreate ? 'Create' : 'Save'}
                  </>
                )}
              </button>
              <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
);

export default SalesTeamsPage;
